import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useAdminAuth } from "@/contexts/AdminAuthContext";
import Logo from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/ui/password-input";
import { Loader2, ShieldCheck, ArrowRight } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import { Doodle, Starburst } from "@/components/marketing/Decor";

export default function AdminLoginPage() {
  const { loginAdmin } = useAdminAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const loc = useLocation();

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const adminData = await loginAdmin(email, password);
      const role = adminData?.role;
      let dest = (loc.state && loc.state.from);
      if (!dest) {
        dest = (role === "franchise" || role === "franchise_staff")
          ? "/franchise/overview"
          : "/admin/dashboard";
      }
      navigate(dest, { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="app-light mkt-root mkt-paper-noise min-h-screen flex items-center justify-center px-4 py-10 relative overflow-hidden">
      <Toaster richColors position="top-center" />
      <Doodle.Star className="hidden md:block absolute left-8 top-8 h-10 w-10 text-black/80" />
      <Doodle.Bolt className="hidden md:block absolute right-10 bottom-12 h-12 w-8 text-black rotate-12" />
      <Starburst className="hidden md:block absolute right-1/4 top-12 h-7 w-7 text-[var(--graffiti-yellow-deep)]" />

      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-6">
          <Link to="/" aria-label="City DOOH home" className="inline-flex focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-black rounded-md"><Logo size={150} light /></Link>
          <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-black">
            <ShieldCheck className="h-3.5 w-3.5" />
            Admin Portal
          </div>
        </div>
        <div className="mkt-sticker p-7 relative">
          <span className="mkt-tape absolute -top-4 left-6">Admin Login</span>
          <h1 className="font-display uppercase text-3xl mt-3 leading-none">
            Sign <span className="mkt-highlight">in</span>
          </h1>
          <p className="text-sm text-black/70 mt-2">Admin access only.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div className="space-y-1.5">
              <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="admin-login-email" className="h-11 border-2 border-black bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label className="uppercase text-[11px] font-bold tracking-wider text-black">Password</Label>
              <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} data-testid="admin-login-password" className="h-11 border-2 border-black bg-white" />
            </div>
            {err && (
              <p className="text-sm font-bold text-[#8b1212] bg-[#fbd5d5] border-2 border-[#8b1212] rounded-md px-3 py-2" data-testid="admin-login-error">{err}</p>
            )}
            <button type="submit" className="mkt-btn w-full justify-center" disabled={busy} data-testid="admin-login-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Sign in <ArrowRight className="h-4 w-4" /></>)}
            </button>
          </form>
          <div className="mt-5 text-xs text-black/70 text-center uppercase tracking-wider font-semibold">
            Super Admin?{" "}
            <Link to="/superadmin/login" className="font-bold text-black underline decoration-[var(--graffiti-yellow)] decoration-4 underline-offset-2">Sign in here</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
